import {
  register,
  requestResetPassword,
  resetPassword,
  signIn,
  changeEmailPassword,
  exchangeToken,
} from "@/services/auth";
import { LoginType, Role } from "@/shared/constants/enum";
import { ROUTERS } from "@/shared/constants/router";
import {
  ChangeEmailPasswordRequest,
  // ExchangeTokenResponse,
  LoginSite,
  RegisterRequest,
  RequestResetPasswordRequest,
  ResetPasswordRequest,
  SignInRequest,
  SignInResponse,
} from "@/shared/types/auth";
import { User } from "@/shared/types/user";
import { clearAllStores } from "@/store/clearStores";
import { useLoginAttemptsStore } from "@/store/loginAttempts";
import { useAuthStore } from "@/store/useAuth";
import { useConfigStore } from "@/store/useConfig";
import { useUserStore } from "@/store/useUser";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useState, useRef } from "react";

export function useSignIn() {
  const { setAccessToken, setRefreshToken } = useAuthStore();
  const { setLoginAttempts, loginAttempts } = useLoginAttemptsStore();
  const { setAccountType } = useUserStore();
  const navigate = useNavigate();
  return useMutation<SignInResponse, AxiosError, SignInRequest>({
    mutationKey: ["useSignIn"],
    mutationFn: signIn,
    onSuccess: (res) => {
      if (!res) return;
      if (!res.user.role.includes(Role.STUDENT))
        throw new Error(
          "🧑‍🎓 Oops! This zone is for students only. Please log in through the teacher portal 👩‍🏫✨"
        );
      setLoginAttempts(3);
      setAccessToken(res.accessToken);
      setRefreshToken(res.refreshToken);
      setAccountType(LoginType.MANUAL);
      navigate({
        to: ROUTERS.HOME,
        replace: true,
      });
    },
    onError: () => {
      if (loginAttempts > 0) {
        setLoginAttempts(loginAttempts - 1);
      }
    },
  });
}

export function useSignInLMS() {
  const navigate = useNavigate();
  const { setAccessToken, setRefreshToken } = useAuthStore();
  const { setUser, setAccountType } = useUserStore();
  const [lmsError, setLmsError] = useState<string | null>(null);
  const authWindowRef = useRef<Window | null>(null);

  const loginLMSMutation = useMutation<
    SignInResponse,
    AxiosError,
    SignInRequest
  >({
    mutationKey: ["useSignIn"],
    mutationFn: signIn,
    onSuccess: (res: SignInResponse) => {
      if (res.authorizationUrl && authWindowRef.current) {
        authWindowRef.current.location.href = res.authorizationUrl;
      }
    },
    onError: (error: any) => {
      authWindowRef.current?.close();
      setLmsError(
        error.message || "An unexpected error occurred. Please try again."
      );
    },
  });

  const exchangeTokenMutation = useMutation({
    mutationFn: exchangeToken,
    onSuccess: (response) => {
      setLmsError(null);
      console.log(response);
      
      if (response.accessToken) {
        if (!response.user.role.includes(Role.STUDENT))
          throw new Error(
            "🧑‍🎓 Oops! This zone is for students only. Please log in through the teacher portal 👩‍🏫✨"
          );
        setAccessToken(response.accessToken);
        setRefreshToken(response.refreshToken);
        setUser(response.user);
        setAccountType(LoginType.MOODLE);
        navigate({
          to: ROUTERS.HOME,
          replace: true,
        });
      }
    },
    onError: (error: any) => {
      setLmsError(error.message || "Failed to exchange token");
    },
  });

  useEffect(() => {
    const checkRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const redirectParam = urlParams.get("redirect");
      const successParam = urlParams.get('success')
      if (successParam) {
        exchangeTokenMutation.mutate(successParam)
        return
      }
      if (redirectParam) {
        try {
          const decoded = decodeURIComponent(redirectParam);
          const params = new URLSearchParams(
            decoded.includes("?") ? decoded.split("?")[1] : decoded
          );

          if (params.get("code") === "fail") {
            const error = params.get("error");
            const message = params.get("message");

            if (error === "user_not_found") {
              setLmsError("User not found");
            } else if (error === "wrong_site") {
              setLmsError("Unauthorized access");
            } else {
              setLmsError(
                message?.replace(/\+/g, " ") || `Login failed: ${error}`
              );
            }
            return;
          }

          const token = decoded.split("success=")[1];
          if (token) {
            exchangeTokenMutation.mutate(token);
          }
        } catch (e) {
          console.error("Failed to parse redirect param", e);
        }
      }
    };
    checkRedirect();
  }, []);

  const handleLmsLogin = () => {
    setLmsError(null);
    authWindowRef.current = window.open("", "_blank");
    loginLMSMutation.mutate({
      createdType: LoginType.MOODLE,
      site: LoginSite.STUDENT,
      website: import.meta.env.VITE_OAUTH_SITE_CALLBACK,
    });
  };

  return {
    handleLmsLogin,
    isLoading: loginLMSMutation.isPending || exchangeTokenMutation.isPending,
    lmsError,
  };
}

export function useSignOut() {
  const navigate = useNavigate();
  return useMutation({
    mutationKey: ["useSignOut"],
    mutationFn: async () => {
      clearAllStores();
    },
    onSuccess: () => {
      navigate({
        to: ROUTERS.SIGN_IN,
        replace: true,
      });
    },
  });
}

export function useRegister() {
  return useMutation<User, AxiosError, RegisterRequest>({
    mutationKey: ["useRegister"],
    mutationFn: register,
  });
}

export function useRequestResetPassword() {
  const { setIsSendEmail } = useConfigStore();
  return useMutation<string, AxiosError, RequestResetPasswordRequest>({
    mutationKey: ["useRequestResetPassword"],
    mutationFn: requestResetPassword,
    onMutate: () => {
      setIsSendEmail(true);
    },
  });
}
export function useResetPassword() {
  const { setIsSendEmail } = useConfigStore();
  return useMutation<string, AxiosError, ResetPasswordRequest>({
    mutationKey: ["useRequestResetPassword"],
    mutationFn: resetPassword,
    onMutate: () => {
      setIsSendEmail(false);
    },
  });
}

export function useChangeEmailPassword() {
  return useMutation<string, AxiosError, ChangeEmailPasswordRequest>({
    mutationKey: ["useChangeEmailPassword"],
    mutationFn: changeEmailPassword,
  });
}
